/* eslint-disable import/no-default-export */
/* eslint-disable jsdoc/require-jsdoc */
import React, { Component } from 'react';

import { Box, Text } from '@opendoor/bricks/core';
import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import styled from '@emotion/styled';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { CXA_EMAIL_NEW, CXA_EMAIL } from 'shared/helpers/globals';

interface ICalendlyParams {
  utm_content?: string;
  utm_source?: string;
  phone?: string;
  name?: string;
  email?: string;
}

export interface IProps {
  calendlyId?: string | null;
  calendar?: string;
  params?: ICalendlyParams;
  onError?: (exception: string) => void;
}

export class CalendlyEmbed extends Component<IProps> {
  public componentDidMount() {
    loadCalendly();
  }

  public componentWillUnmount() {
    unloadCalendly(this.props.onError);
  }

  public render() {
    // Format the phone number correctly before passing to Calendly
    // If the phone number already had country code (which has a '+' at the beginning), return the number.
    // Otherwise add the default '1' for US country code in front of it
    const getPhoneNumber = () => {
      if (this.props.params && this.props.params.phone) {
        const phoneNumber = this.props.params.phone;
        return phoneNumber.startsWith('+') ? phoneNumber : `1${phoneNumber}`;
      }
    };

    // Calendly use a1 for phone number prefilling.
    // https://help.calendly.com/hc/en-us/articles/226766767-Pre-populate-invitee-information-on-the-scheduling-page#pre-populate-invitee-information-on-the-scheduling-page-0-0
    const params = {
      ...this.props.params,
      a1: getPhoneNumber(),
    };

    // delete a key in the param if the value is falsey. Otherwise, Calendly will render the value as a string, for example, "undefined"
    for (const key in params) {
      // @ts-expect-error - SellDirect to resolve previously existing error in legacy code
      if (!params[key]) {
        // @ts-expect-error - SellDirect to resolve previously existing error in legacy code
        delete params[key];
      }
    }

    // Convert '+' to encoded space ('%20') for use in calendly prefill
    // @ts-expect-error - SellDirect to resolve previously existing error in legacy code
    const queryParams = new URLSearchParams(params).toString().replace('+', '%20');

    const calendlyPath = this.props.calendlyId
      ? `reschedulings/${this.props.calendlyId}`
      : this.props.calendar;

    /**
     * In reality, this calculation should be 100vh - 80px since the header of the modal
     * where the Calendly iFrame is always 80px.
     *
     * However, on mobile 100vh includes the space of the native mobile nav. Therefore
     * we need to account for that.
     *
     * This is a quick fix to solve the problem asap while we find a better approach.
     */
    const mobileHeight = 'calc(100vh - 215px)';

    return this.props.calendar ? (
      <CalendlyInlineWidget
        height={[mobileHeight, null, '70vh']}
        width="100%"
        maxHeight="921px"
        className="calendly-inline-widget"
        data-url={`https://calendly.com/${calendlyPath}?${queryParams}`}
      />
    ) : (
      <ErrorLoadingCalendar />
    );
  }
}

const CalendlyInlineWidget = styled(Box)`
  .spot-picker {
    min-height: 'unset';
  }

  ${media.smallerThan.XS} {
    ::-webkit-scrollbar {
      width: 0px;
      background: transparent;
    }
  }
`;

const ErrorLoadingCalendar = () => {
  const {
    assertions: { isCepEmailUpdatesEnabled },
  } = useExperimentationContext();
  const email = isCepEmailUpdatesEnabled ? CXA_EMAIL_NEW : CXA_EMAIL;
  return (
    <Box>
      <Text fontSize="s0" lineHeight="s00" mb={4}>
        Sorry, there was an error loading calendar information to schedule your appointment. Our
        team has been notified.
      </Text>
      <Text fontSize="s0" lineHeight="s00">
        {/* eslint-disable-next-line @opendoor/no-html-elements */}
        Please contact <a href={`mailto:${email}`}>{email}</a> to set up an appointment
      </Text>
    </Box>
  );
};

export const test = { ErrorLoadingCalendar };

export const loadCalendly = () => {
  const script = document.createElement('script');
  script.id = 'opendoor-calendly';
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://assets.calendly.com/assets/external/widget.js';
  script.onload = () => {
    const iframes = document.querySelectorAll('.calendly-inline-widget iframe');
    iframes.forEach((iframe) => iframe.setAttribute('tabindex', '0'));
  };
  document.getElementsByTagName('head')[0].appendChild(script);
};

export const unloadCalendly = (onError?: (exception: string) => void) => {
  const script = document.querySelector('script#opendoor-calendly');
  if (script) {
    if (typeof script.remove !== undefined) {
      script.remove();
    } else if (script.parentNode) {
      const parentNode = script.parentNode;
      parentNode.removeChild(script);
    } else {
      // We do not expect to get here since loadCalendly adds a child onto an element,
      // so parentNode shouldn't be nil.
      onError && onError('Unable to unload calendly');
    }
  }
};
